// src/components/character_creation/FinalizeCreation.jsx
// Читает черновик из Redux (useCharacterDraft). props.characterData не используется.
// Все обращения к background — через data?.background / data.background (data = draft || {}).

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Person as PersonIcon,
  Groups as GroupsIcon,
  Work as WorkIcon,
  Psychology as PsychologyIcon,
  MilitaryTech as MilitaryTechIcon,
  Translate as TranslateIcon,
  Male as MaleIcon,
  Female as FemaleIcon,
  Transgender as TransgenderIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Edit as EditIcon,
  Create as CreateIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';

import { useCharacterDraft } from '../../hooks/useCharacterDraft';
import {
  isLegendEmpty,
  legendToString,
  legendLength,
} from '../../utils/characterDraft';

const FinalizeCreation = ({
  onCreate,
  loading = false,
  onEditStep = null,
}) => {
  const { draft } = useCharacterDraft();
  const data = draft || {};

  const [validationErrors, setValidationErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    race: true,
    template: true,
    background: true,
    skills: true,
  });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  useEffect(() => {
    const errors = [];
    const warningsList = [];

    if (!data.race && !data.race_id) errors.push('Не выбрана раса');
    if (!data.character_template && !data.template_id) errors.push('Не выбран шаблон персонажа');
    if (!data.name?.trim()) errors.push('Не указано имя персонажа');

    if (isLegendEmpty(data.background)) {
      errors.push('Не указана предыстория');
    }

    if (!data.skills || data.skills.length === 0) {
      errors.push('Не выбраны навыки');
    }
    if (data.skills && data.skills.length > 7) {
      errors.push('Превышен лимит навыков (максимум 7)');
    }
    if (data.skills && data.skills.length < 3) {
      warningsList.push('Выбрано мало навыков. Рекомендуется не менее 3');
    }

    if (legendLength(data.background) < 100) {
      warningsList.push('Предыстория довольно короткая. Рекомендуется добавить больше деталей');
    }

    setValidationErrors(errors);
    setWarnings(warningsList);
  }, [data]);

  const toggleSection = (section) =>
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));

  const getGenderIcon = () => {
    switch (data.gender) {
      case 'male': return <MaleIcon />;
      case 'female': return <FemaleIcon />;
      default: return <TransgenderIcon />;
    }
  };

  const getGenderLabel = () => {
    const labels = {
      male: 'Мужской',
      female: 'Женский',
      neutral: 'Нейтральный',
      androgynous: 'Андрогинный',
      fluid: 'Гендер-флюид',
    };
    return labels[data.gender] || 'Не указан';
  };

  const getRaceName = () =>
    data.race?.name || data.race_info?.name || 'Не выбрана';

  const getTemplateName = () =>
    data.character_template?.name || data.template_info?.name || 'Не выбран';

  const handleOpenConfirmDialog = () => setConfirmDialogOpen(true);
  const handleCloseConfirmDialog = () => setConfirmDialogOpen(false);
  const handleConfirmCreate = () => {
    setConfirmDialogOpen(false);
    if (onCreate) onCreate();
  };
  const handleEditStep = (stepIndex) => {
    if (onEditStep) onEditStep(stepIndex);
  };

  const canCreate = validationErrors.length === 0;
  const backgroundText = legendToString(data.background);
  const skillsCount = data.skills?.length || 0;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Завершение создания</Typography>
      <Typography variant="body1" paragraph color="text.secondary">
        Проверьте все данные вашего персонажа перед созданием.
      </Typography>

      <Card
        sx={{
          mb: 3,
          border: canCreate ? '2px solid' : '1px solid',
          borderColor: canCreate ? 'success.main' : 'divider',
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
              {canCreate ? (
                <><CheckCircleIcon color="success" sx={{ mr: 1 }} />Персонаж готов к созданию</>
              ) : (
                <><ErrorIcon color="error" sx={{ mr: 1 }} />Требуется исправление ошибок</>
              )}
            </Typography>
            <Chip
              label={`${5 - validationErrors.length}/5`}
              color={canCreate ? 'success' : 'error'}
              variant="outlined"
            />
          </Box>
          <LinearProgress
            variant="determinate"
            value={((5 - validationErrors.length) / 5) * 100}
            color={canCreate ? 'success' : 'error'}
            sx={{ height: 8, borderRadius: 4, mb: 1 }}
          />
        </CardContent>
      </Card>

      {validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>Обнаружены ошибки:</Typography>
          <List dense>
            {validationErrors.map((error, index) => (
              <ListItem key={index}>
                <ListItemIcon><ErrorIcon color="error" /></ListItemIcon>
                <ListItemText primary={error} />
              </ListItem>
            ))}
          </List>
        </Alert>
      )}

      {warnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>Рекомендации:</Typography>
          <List dense>
            {warnings.map((warning, index) => (
              <ListItem key={index}>
                <ListItemIcon><WarningIcon color="warning" /></ListItemIcon>
                <ListItemText primary={warning} />
              </ListItem>
            ))}
          </List>
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <PersonIcon sx={{ mr: 1 }} />
          Обзор персонажа
        </Typography>

        {/* Раса */}
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => toggleSection('race')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ mr: 1 }}><GroupsIcon /></Box>
                <Typography variant="subtitle1">Раса: {getRaceName()}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {data.race || data.race_id ? (
                  <Chip label="Выбрано" color="success" size="small" variant="outlined" />
                ) : (
                  <Chip label="Не выбрано" color="error" size="small" variant="outlined" />
                )}
                <IconButton size="small" sx={{ ml: 1 }}>
                  {expandedSections.race ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
            </Box>
            <Collapse in={expandedSections.race}>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" paragraph>
                  {data.race?.description || 'Описание отсутствует'}
                </Typography>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button size="small" startIcon={<EditIcon />} onClick={() => handleEditStep(0)} variant="outlined">
                    Изменить расу
                  </Button>
                </Box>
              </Box>
            </Collapse>
          </CardContent>
        </Card>

        {/* Шаблон */}
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => toggleSection('template')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ mr: 1 }}><WorkIcon /></Box>
                <Typography variant="subtitle1">Шаблон: {getTemplateName()}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {data.character_template || data.template_id ? (
                  <Chip label="Выбрано" color="success" size="small" variant="outlined" />
                ) : (
                  <Chip label="Не выбрано" color="error" size="small" variant="outlined" />
                )}
                <IconButton size="small" sx={{ ml: 1 }}>
                  {expandedSections.template ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
            </Box>
            <Collapse in={expandedSections.template}>
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Темперамент:</Typography>
                    <Chip label={data.temperament || 'Не указан'} icon={<PsychologyIcon />} variant="outlined" size="small" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Гендер:</Typography>
                    <Chip label={getGenderLabel()} icon={getGenderIcon()} variant="outlined" size="small" />
                  </Grid>
                </Grid>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button size="small" startIcon={<EditIcon />} onClick={() => handleEditStep(1)} variant="outlined">
                    Изменить шаблон
                  </Button>
                </Box>
              </Box>
            </Collapse>
          </CardContent>
        </Card>

        {/* Имя и предыстория */}
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => toggleSection('background')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ mr: 1 }}><TranslateIcon /></Box>
                <Typography variant="subtitle1">Имя и предыстория</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {data.name && !isLegendEmpty(data.background) ? (
                  <Chip label="Заполнено" color="success" size="small" variant="outlined" />
                ) : (
                  <Chip label="Не заполнено" color="error" size="small" variant="outlined" />
                )}
                <IconButton size="small" sx={{ ml: 1 }}>
                  {expandedSections.background ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
            </Box>
            <Collapse in={expandedSections.background}>
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" color="text.secondary">Имя персонажа:</Typography>
                    <Typography variant="body1">{data.name || 'Не указано'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <Typography variant="subtitle2" color="text.secondary">Длина предыстории:</Typography>
                    <Chip
                      label={`${legendLength(data.background) || 0} символов`}
                      color={legendLength(data.background) > 100 ? 'success' : 'warning'}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                </Grid>

                {!isLegendEmpty(data.background) && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Предварительный просмотр:
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, mt: 1, maxHeight: 200, overflow: 'auto' }}>
                      <Typography variant="body2" color="text.secondary" whiteSpace="pre-wrap">
                        {backgroundText.substring(0, 300) + (backgroundText.length > 300 ? '...' : '')}
                      </Typography>
                    </Paper>
                  </Box>
                )}

                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button size="small" startIcon={<EditIcon />} onClick={() => handleEditStep(2)} variant="outlined">
                    Изменить имя и предысторию
                  </Button>
                </Box>
              </Box>
            </Collapse>
          </CardContent>
        </Card>

        {/* Навыки */}
        <Card variant="outlined">
          <CardContent>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => toggleSection('skills')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ mr: 1 }}><MilitaryTechIcon /></Box>
                <Typography variant="subtitle1">Навыки и способности</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Chip
                  label={`${skillsCount}/7`}
                  color={skillsCount > 0 ? 'primary' : 'error'}
                  size="small"
                  variant="outlined"
                />
                <IconButton size="small" sx={{ ml: 1 }}>
                  {expandedSections.skills ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
            </Box>
            <Collapse in={expandedSections.skills}>
              <Box sx={{ mt: 2 }}>
                {skillsCount > 0 ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {data.skills.slice(0, 10).map((skill, index) => (
                      <Chip
                        key={skill?.id || index}
                        label={skill?.name || skill}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                    {skillsCount > 10 && (
                      <Chip label={`+${skillsCount - 10} еще`} size="small" variant="outlined" color="info" />
                    )}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">Навыки не выбраны</Typography>
                )}
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button size="small" startIcon={<EditIcon />} onClick={() => handleEditStep(3)} variant="outlined">
                    Изменить навыки
                  </Button>
                </Box>
              </Box>
            </Collapse>
          </CardContent>
        </Card>
      </Paper>

      <Paper sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => handleEditStep(3)}
          disabled={loading}
        >
          Вернуться к навыкам
        </Button>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="error"
            onClick={handleOpenConfirmDialog}
            disabled={!canCreate || loading}
            startIcon={<InfoIcon />}
          >
            Проверить данные
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenConfirmDialog}
            disabled={!canCreate || loading}
            startIcon={loading ? <CircularProgress size={20} /> : <CreateIcon />}
            size="large"
          >
            {loading ? 'Создание...' : 'Создать персонажа'}
          </Button>
        </Box>
      </Paper>

      <Dialog open={confirmDialogOpen} onClose={handleCloseConfirmDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircleIcon color="primary" sx={{ mr: 1 }} />
            Подтверждение создания персонажа
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText paragraph>
            Вы уверены, что хотите создать персонажа с выбранными параметрами?
          </DialogContentText>
          <Divider sx={{ my: 2 }} />
          <List dense>
            <ListItem>
              <ListItemIcon><GroupsIcon /></ListItemIcon>
              <ListItemText primary="Раса" secondary={getRaceName()} />
            </ListItem>
            <ListItem>
              <ListItemIcon><WorkIcon /></ListItemIcon>
              <ListItemText primary="Шаблон" secondary={getTemplateName()} />
            </ListItem>
            <ListItem>
              <ListItemIcon><TranslateIcon /></ListItemIcon>
              <ListItemText primary="Имя" secondary={data.name || 'Не указано'} />
            </ListItem>
            <ListItem>
              <ListItemIcon><MilitaryTechIcon /></ListItemIcon>
              <ListItemText primary="Навыки" secondary={`${skillsCount} навыков`} />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} disabled={loading}>Отмена</Button>
          <Button
            onClick={handleConfirmCreate}
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <CreateIcon />}
          >
            {loading ? 'Создание...' : 'Подтвердить создание'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FinalizeCreation;
